import { PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Breadcrumbs, Tabs } from "@/components/page";
import { propertyTypeIcons, propertyTypeLabels } from "@/components/properties/Properties";
import { OptionMenu, randomColor } from "@/components/properties/SelectEditor";
import { AddRow, ColorDot, InlineName, Section } from "@/components/SettingsParts";
import { ReorderList } from "@/components/ReorderList";
import { Button, Field, Tag, UserChip } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/Modal";
import { api, type ListKind } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useUsers } from "@/lib/queries";
import type { EntityType, ListItem, Property, PropertyType, Role, Stage, StageKind } from "@/lib/types";
import { cn } from "@/lib/utils";

function useRun() {
  const { pid } = useProject();
  const qc = useQueryClient();
  return async <T,>(p: Promise<T>): Promise<T | undefined> => {
    try {
      return await p;
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
      return undefined;
    } finally {
      await qc.invalidateQueries({ queryKey: keys.project(pid) });
    }
  };
}

function GeneralSettings() {
  const { pid, bundle, isOwner } = useProject();
  const run = useRun();
  const navigate = useNavigate();
  const { toast } = useFeedback();
  const [form, setForm] = useState({ name: bundle.project.name, key: bundle.project.key, description: bundle.project.description });
  const [deleting, setDeleting] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  return (
    <div className="space-y-10">
      <Section title="Project details">
        <div className="max-w-lg space-y-4">
          <Field label="Name">
            <input value={form.name} readOnly={!isOwner} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" dir="auto" />
          </Field>
          <Field label="Key" hint="Changing the key renames every task reference (e.g. UNI-12 → WEB-12).">
            <input
              value={form.key}
              readOnly={!isOwner}
              onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) })}
              className="field font-mono"
            />
          </Field>
          <Field label="Description">
            <textarea value={form.description} readOnly={!isOwner} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="field resize-none" dir="auto" />
          </Field>
          {isOwner && (
            <Button
              variant="primary"
              onClick={async () => {
                if ((await run(api.updateProject(pid, form))) !== undefined) toast("Project updated", "success");
              }}
            >
              Save changes
            </Button>
          )}
        </div>
      </Section>
      {isOwner && (
        <Section title="Danger zone">
          <div className="flex max-w-lg items-center justify-between gap-4 rounded-lg border border-danger/40 p-4">
            <div className="text-sm">
              <div className="font-medium">Delete this project</div>
              <div className="text-muted">Removes all epics, sprints, tasks, comments and files. This can't be undone.</div>
            </div>
            <Button variant="danger" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          </div>
          <Modal
            open={deleting}
            onClose={() => setDeleting(false)}
            title="Delete project"
            size="sm"
            footer={
              <>
                <Button onClick={() => setDeleting(false)}>Cancel</Button>
                <Button
                  variant="danger"
                  disabled={confirmName !== bundle.project.name}
                  onClick={async () => {
                    await api.deleteProject(pid);
                    navigate("/");
                  }}
                >
                  Delete forever
                </Button>
              </>
            }
          >
            <p className="mb-3 text-sm text-muted">
              Type <span className="font-medium text-fg">{bundle.project.name}</span> to confirm.
            </p>
            <input autoFocus value={confirmName} onChange={(e) => setConfirmName(e.target.value)} className="field" dir="auto" />
          </Modal>
        </Section>
      )}
    </div>
  );
}

const roleLabels: Record<Role, string> = { owner: "Owner", member: "Member", viewer: "Viewer" };
const roleHints: Record<Role, string> = {
  owner: "Full control, manages members, stages and statuses",
  member: "Creates and edits epics, sprints, tasks and files",
  viewer: "Read-only access; can comment",
};

function MembersSettings() {
  const { pid, members, isOwner, me } = useProject();
  const users = useUsers();
  const run = useRun();
  const { confirm } = useFeedback();
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<Role>("member");
  const candidates = (users.data ?? []).filter((u) => u.isActive && !members.some((m) => m.id === u.id));

  return (
    <Section title="Members" description="People who can access this project. System admins can access every project.">
      {isOwner && (
        <form
          className="flex flex-wrap items-end gap-2 rounded-lg border border-line p-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!userId) return;
            await run(api.setMember(pid, userId, role));
            setUserId("");
          }}
        >
          <Field label="Add a person">
            <select value={userId} onChange={(e) => setUserId(e.target.value)} className="field min-w-56">
              <option value="">Choose a user…</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value as Role)} className="field">
              {(Object.keys(roleLabels) as Role[]).map((r) => (
                <option key={r} value={r}>
                  {roleLabels[r]}
                </option>
              ))}
            </select>
          </Field>
          <Button type="submit" variant="primary" disabled={!userId}>
            Add
          </Button>
          {candidates.length === 0 && users.data && <p className="w-full text-xs text-faint">Everyone is already a member. Admins create new accounts under Manage users.</p>}
        </form>
      )}
      <div className="divide-y divide-line rounded-lg border border-line">
        {members.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
            <UserChip id={m.id} name={m.name} size={26} className="min-w-40 flex-1 text-sm font-medium" />
            <span className="text-sm text-muted">{m.email}</span>
            {isOwner ? (
              <select
                value={m.role}
                onChange={async (e) => {
                  const next = e.target.value as Role;
                  if (next === "viewer") {
                    const ok = await confirm({ title: `Make ${m.name} a viewer?`, message: "They'll be unassigned from their tasks in this project and their timers will stop.", confirmLabel: "Change role" });
                    if (!ok) return;
                  }
                  run(api.setMember(pid, m.id, next));
                }}
                className="field w-auto py-1"
                title={roleHints[m.role]}
              >
                {(Object.keys(roleLabels) as Role[]).map((r) => (
                  <option key={r} value={r}>
                    {roleLabels[r]}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm">{roleLabels[m.role]}</span>
            )}
            {isOwner && (
              <button
                type="button"
                className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
                title="Remove from project"
                onClick={async () => {
                  const ok = await confirm({
                    title: m.id === me.id ? "Leave this project?" : `Remove ${m.name}?`,
                    message: "They'll lose access and be unassigned from all tasks in this project.",
                    confirmLabel: "Remove",
                    danger: true,
                  });
                  if (ok) run(api.removeMember(pid, m.id));
                }}
              >
                <XMarkIcon className="size-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      <ul className="space-y-0.5 text-xs text-faint">
        {(Object.keys(roleLabels) as Role[]).map((r) => (
          <li key={r}>
            <span className="font-medium text-muted">{roleLabels[r]}:</span> {roleHints[r]}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function PropertyCard({ property }: { property: Property }) {
  const { pid, canEdit } = useProject();
  const run = useRun();
  const { confirm } = useFeedback();
  const Icon = propertyTypeIcons[property.type];
  return (
    <div className="min-w-0 flex-1 rounded-lg border border-line p-2">
      <div className="flex items-center gap-2">
        <Icon className="size-4 shrink-0 text-muted" />
        <InlineName value={property.name} disabled={!canEdit} onSave={(name) => run(api.updateProperty(pid, property.id, name))} className="font-medium" />
        <span className="text-xs text-faint">{propertyTypeLabels[property.type]}</span>
        {canEdit && (
          <button
            type="button"
            className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
            title="Delete property"
            onClick={async () => {
              const ok = await confirm({
                title: `Delete "${property.name}"?`,
                message: `The property and its values will be removed from every ${property.entityType}.`,
                confirmLabel: "Delete",
                danger: true,
              });
              if (ok) run(api.deleteProperty(pid, property.id));
            }}
          >
            <TrashIcon className="size-4" />
          </button>
        )}
      </div>
      {property.type !== "date" && (
        <div className="mt-2 ps-2">
          <ReorderList
            items={property.options}
            disabled={!canEdit}
            onReorder={(ids) => run(api.reorderOptions(pid, property.id, ids))}
            render={(o) => (
              <div className="group flex items-center gap-2">
                <Tag color={o.color}>{o.label}</Tag>
                {canEdit && <OptionMenu option={o} onUpdate={(b) => run(api.updateOption(pid, o.id, b))} onDelete={() => run(api.deleteOption(pid, o.id))} />}
              </div>
            )}
          />
          <AddRow
            placeholder="Add an option"
            disabled={!canEdit}
            onAdd={(label) => run(api.createOption(pid, property.id, { label, color: randomColor(property.options.map((o) => o.color)) }))}
          />
        </div>
      )}
    </div>
  );
}

function PropertiesSettings() {
  const { pid, propsFor, canEdit } = useProject();
  const run = useRun();
  const [entity, setEntity] = useState<EntityType>("task");
  const [name, setName] = useState("");
  const [type, setType] = useState<PropertyType>("select");
  const props = propsFor(entity);

  return (
    <Section title="Properties" description="Custom fields shared by every item of the same kind. Members and owners can edit them.">
      <div className="flex gap-1">
        {(["epic", "sprint", "task"] as EntityType[]).map((e) => (
          <button key={e} type="button" onClick={() => setEntity(e)} className={cn("h-7 rounded-full px-3 text-[13px] capitalize", entity === e ? "bg-fg text-canvas" : "text-muted hover:bg-hover")}>
            {e} properties
          </button>
        ))}
      </div>
      <ReorderList items={props} disabled={!canEdit} onReorder={(ids) => run(api.reorderProperties(pid, ids))} render={(p) => <PropertyCard property={p} />} className="space-y-2" />
      {props.length === 0 && <p className="text-sm text-muted">No {entity} properties yet.</p>}
      {canEdit && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim()) return;
            await run(api.createProperty(pid, { entityType: entity, name: name.trim(), type }));
            setName("");
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${entity} property name`} className="field max-w-64" dir="auto" />
          <select value={type} onChange={(e) => setType(e.target.value as PropertyType)} className="field w-auto">
            {(Object.keys(propertyTypeLabels) as PropertyType[]).map((t) => (
              <option key={t} value={t}>
                {propertyTypeLabels[t]}
              </option>
            ))}
          </select>
          <Button type="submit" icon={PlusIcon} disabled={!name.trim()}>
            Add property
          </Button>
        </form>
      )}
    </Section>
  );
}

const stageKindLabels: Record<StageKind, string> = {
  normal: "Regular stage",
  in_progress: "In Progress (sets start time)",
  qa: "QA (sets QA time)",
  done: "Done (counts as complete)",
};

function StagesSettings() {
  const { pid, bundle, isOwner } = useProject();
  const run = useRun();
  const [removing, setRemoving] = useState<Stage | null>(null);
  const [moveTo, setMoveTo] = useState("");

  return (
    <Section
      title="Stages"
      description="Board columns for tasks. Mark one stage as In Progress and one as QA to record those timestamps automatically, and one as Done for progress tracking."
    >
      <ReorderList
        items={bundle.stages}
        disabled={!isOwner}
        onReorder={(ids) => run(api.reorderStages(pid, ids))}
        render={(s) => (
          <>
            <ColorDot color={s.color} disabled={!isOwner} onChange={(color) => run(api.updateStage(pid, s.id, { color }))} />
            <InlineName value={s.name} disabled={!isOwner} onSave={(name) => run(api.updateStage(pid, s.id, { name }))} />
            <select
              value={s.kind}
              disabled={!isOwner}
              onChange={(e) => run(api.updateStage(pid, s.id, { kind: e.target.value as StageKind }))}
              className="field w-auto py-1 text-[13px]"
            >
              {(Object.keys(stageKindLabels) as StageKind[]).map((k) => (
                <option key={k} value={k}>
                  {stageKindLabels[k]}
                </option>
              ))}
            </select>
            {isOwner && (
              <button
                type="button"
                className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
                title="Delete stage"
                onClick={() => {
                  setRemoving(s);
                  setMoveTo(bundle.stages.find((x) => x.id !== s.id)?.id ?? "");
                }}
              >
                <TrashIcon className="size-4" />
              </button>
            )}
          </>
        )}
      />
      <AddRow placeholder="Add a stage" disabled={!isOwner} onAdd={(name) => run(api.createStage(pid, { name, color: randomColor(), kind: "normal" }))} />
      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        size="sm"
        title={`Delete "${removing?.name}"?`}
        footer={
          <>
            <Button onClick={() => setRemoving(null)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={bundle.stages.length <= 1}
              onClick={async () => {
                if (removing) await run(api.deleteStage(pid, removing.id, moveTo));
                setRemoving(null);
              }}
            >
              Delete stage
            </Button>
          </>
        }
      >
        <Field label="Move its tasks to">
          <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} className="field">
            {bundle.stages
              .filter((s) => s.id !== removing?.id)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </Field>
      </Modal>
    </Section>
  );
}

function ListSettings({ kind, items, title, description }: { kind: ListKind; items: ListItem[]; title: string; description: string }) {
  const { pid, isOwner } = useProject();
  const run = useRun();
  const { confirm } = useFeedback();
  return (
    <Section title={title} description={description}>
      <ReorderList
        items={items}
        disabled={!isOwner}
        onReorder={(ids) => run(api.reorderListItems(pid, kind, ids))}
        render={(s) => (
          <>
            <ColorDot color={s.color} disabled={!isOwner} onChange={(color) => run(api.updateListItem(pid, kind, s.id, { color }))} />
            <InlineName value={s.name} disabled={!isOwner} onSave={(label) => run(api.updateListItem(pid, kind, s.id, { label }))} />
            {isOwner && (
              <button
                type="button"
                className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
                title="Delete"
                onClick={async () => {
                  if (await confirm({ title: `Delete "${s.name}"?`, message: "Items using this status will show no status.", confirmLabel: "Delete", danger: true }))
                    run(api.deleteListItem(pid, kind, s.id));
                }}
              >
                <TrashIcon className="size-4" />
              </button>
            )}
          </>
        )}
      />
      <AddRow placeholder="Add a status" disabled={!isOwner} onAdd={(label) => run(api.createListItem(pid, kind, { label, color: randomColor() }))} />
    </Section>
  );
}

type SettingsTab = "general" | "members" | "properties" | "workflow";

export function ProjectSettingsPage() {
  const { pid, bundle, isOwner } = useProject();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as SettingsTab) || "general";

  return (
    <>
      <Breadcrumbs items={[{ label: bundle.project.name, to: `/p/${pid}` }, { label: "Settings" }]} />
      <div className="mx-auto max-w-4xl px-6 pt-8 pb-24 md:px-12">
        <h1 className="mb-1 text-[32px] leading-tight font-bold">Settings</h1>
        {!isOwner && <p className="mb-4 text-sm text-muted">Only project owners can change members, stages and statuses.</p>}
        <div className="mt-4 mb-8">
          <Tabs
            value={tab}
            onChange={(t) => setParams({ tab: t }, { replace: true })}
            tabs={[
              { id: "general", label: "General" },
              { id: "members", label: `Members (${bundle.members.length})` },
              { id: "properties", label: "Properties" },
              { id: "workflow", label: "Stages & statuses" },
            ]}
          />
        </div>
        {tab === "general" && <GeneralSettings />}
        {tab === "members" && <MembersSettings />}
        {tab === "properties" && <PropertiesSettings />}
        {tab === "workflow" && (
          <div className="space-y-12">
            <StagesSettings />
            <ListSettings
              kind="sprint-statuses"
              items={bundle.sprintStatuses}
              title="Sprint statuses"
              description="The values available for a sprint's Status field."
            />
            <ListSettings
              kind="assignee-statuses"
              items={bundle.assigneeStatuses}
              title="Assignee statuses"
              description="What each assignee can report about their state while working on a task (e.g. Working, Blocked)."
            />
          </div>
        )}
      </div>
    </>
  );
}
